import settings from 'popups/Settings'
import React, { useCallback, useRef, useState } from 'react'
import type { RefObject } from 'react'
import type {
  InteractionZone,
  Point,
  SelectionTimeRange,
  TimeRange,
} from '../utils/SchedulingUtils'
import { calcPoint, calcTime, snapToHalfHour, validateSelection } from '../utils/SchedulingUtils'

/**
 * Helper to extract native event from React or native event
 */
const getNativeMouseEvent = (e: React.MouseEvent | MouseEvent): MouseEvent => {
  return 'nativeEvent' in e ? (e as React.MouseEvent).nativeEvent : e
}

const getNativeTouchEvent = (e: React.TouchEvent | TouchEvent): TouchEvent => {
  return 'nativeEvent' in e ? (e as React.TouchEvent).nativeEvent : e
}

// Constants
const SLOT_HEIGHT = 34
const MINUTES_PER_SLOT = 30
const CLICK_THRESHOLD = 15

/**
 * Interaction mode type
 */
export type InteractionMode = null | 'selecting' | 'moving' | 'resizing-top' | 'resizing-bottom'

/**
 * Temporary selection area during drag
 */
interface TempSelectionArea {
  top: number
  height: number
}

/**
 * Temporary drag state with conflict information
 */
interface TempDragState extends SelectionTimeRange {
  hasConflict?: boolean
}

/**
 * Parameters for useTimelineInteraction hook
 */
export interface UseTimelineInteractionParams {
  timeLineContainerRef: RefObject<HTMLElement>
  startDate: Date
  onSelectionChange: (start: Date, end: Date, allday?: boolean, index?: number) => void
  checkConflict?: (start: Date, end: Date) => boolean
  multiMode?: boolean
  currentSelection?: SelectionTimeRange | null
  selections?: SelectionTimeRange[]
  currentDuration?: number | null
  isMobileView?: boolean
}

/**
 * Return type for useTimelineInteraction hook
 */
export interface UseTimelineInteractionReturn {
  // State
  interactionMode: InteractionMode
  tempSelectionArea: TempSelectionArea | null
  tempDragState: TempDragState | null
  activeSelectionIndex: number

  // Timeline handlers (accept both React and native events for document-level listeners)
  onTimelineMouseDown: (e: React.MouseEvent | MouseEvent) => void
  onTimelineMouseMove: (e: React.MouseEvent | MouseEvent) => void
  onTimelineMouseUp: (e: React.MouseEvent | MouseEvent) => void
  onTimelineTouchStart: (e: React.TouchEvent | TouchEvent) => void
  onTimelineTouchMove: (e: React.TouchEvent | TouchEvent) => void
  onTimelineTouchEnd: (e: React.TouchEvent | TouchEvent) => void

  // Selection handlers (for drag/resize handles)
  onSelectionMouseDown: (
    e: React.MouseEvent | MouseEvent,
    zone: InteractionZone,
    selectionIndex?: number,
  ) => void
  onSelectionTouchStart: (
    e: React.TouchEvent | TouchEvent,
    zone: InteractionZone,
    selectionIndex?: number,
  ) => void
}

/**
 * useTimelineInteraction Hook
 *
 * Manages all mouse/touch interactions for timeline selection, dragging, and resizing.
 * Handles interaction state and provides callbacks for starting, moving, and ending interactions.
 */
const useTimelineInteraction = ({
  timeLineContainerRef,
  startDate,
  onSelectionChange,
  checkConflict,
  multiMode = false,
  currentSelection = null,
  selections = [],
  currentDuration = null,
  isMobileView = false,
}: UseTimelineInteractionParams): UseTimelineInteractionReturn => {
  // Interaction state
  const [interactionMode, setInteractionMode] = useState<InteractionMode>(null)
  const [tempSelectionArea, setTempSelectionArea] = useState<TempSelectionArea | null>(null)
  const [tempDragState, setTempDragState] = useState<TempDragState | null>(null)
  const [activeSelectionIndex, setActiveSelectionIndex] = useState<number>(-1)

  // Refs for tracking interaction
  const isSelecting = useRef<boolean>(false)
  const selectionStart = useRef<Point | null>(null)
  const selectionEnd = useRef<Point | null>(null)
  const moveStartY = useRef<number | null>(null)
  const originalSelection = useRef<SelectionTimeRange | null>(null)
  const touchOffsetY = useRef<number | null>(null)
  const touchStartPos = useRef<Point | null>(null)
  const hasMoved = useRef<boolean>(false)

  /**
   * Calculate point from mouse/touch event
   */
  const calcPointLocal = useCallback(
    (event: MouseEvent | Touch): Point | null => {
      return calcPoint(event, timeLineContainerRef)
    },
    [timeLineContainerRef],
  )

  /**
   * Update selection with validation and conflict checking
   */
  const updateSelection = useCallback(
    (
      start: Date,
      end: Date,
      preserveDurationOnBoundary = false,
    ): { validated: TimeRange; conflict: boolean } => {
      const validated = validateSelection(start, end, startDate, preserveDurationOnBoundary)
      const conflict = checkConflict ? checkConflict(validated.start, validated.end) : false

      const newSelection: TempDragState = {
        start: validated.start,
        end: validated.end,
        allday: false,
        hasConflict: conflict,
      }

      setTempDragState(newSelection)
      return { validated, conflict }
    },
    [startDate, checkConflict],
  )

  /**
   * Get previous duration or default
   */
  const getPreviousDuration = useCallback((): number => {
    if (currentDuration != null) {
      return currentDuration * 60 * 1000
    }

    if (currentSelection && currentSelection.start && currentSelection.end) {
      return currentSelection.end.getTime() - currentSelection.start.getTime()
    }

    const defaultDuration = settings.getValue('calendar').defaultDuration
    return defaultDuration * 60 * 1000
  }, [currentDuration, currentSelection])

  /**
   * Finalize selection and notify parent
   */
  const finalizeSelection = useCallback(
    (start: Date, end: Date, allday = false): void => {
      if (onSelectionChange) {
        const index = multiMode ? activeSelectionIndex : undefined
        onSelectionChange(start, end, allday, index)
      }
    },
    [onSelectionChange, multiMode, activeSelectionIndex],
  )

  // ========================================================================
  // Selection Interaction Handlers
  // ========================================================================

  const startNewSelection = useCallback(
    (pos: Point): void => {
      setTempDragState(null)
      setInteractionMode('selecting')
      isSelecting.current = true
      selectionStart.current = pos
      selectionEnd.current = pos
      if (isMobileView) {
        touchStartPos.current = { x: pos.x, y: pos.y }
        hasMoved.current = false
      }
    },
    [isMobileView],
  )

  const handleSelectingMove = useCallback((pos: Point): void => {
    if (!isSelecting.current || !selectionStart.current) return

    selectionEnd.current = pos

    let startY = Math.min(selectionStart.current.y, pos.y)
    let endY = Math.max(selectionStart.current.y, pos.y)

    startY = Math.floor(startY / SLOT_HEIGHT) * SLOT_HEIGHT
    endY = Math.ceil(endY / SLOT_HEIGHT) * SLOT_HEIGHT

    setTempSelectionArea({
      top: startY,
      height: endY - startY,
    })
  }, [])

  const handleSelectingEnd = useCallback(
    (pos: Point | null): void => {
      if (!isSelecting.current) {
        setInteractionMode(null)
        setTempSelectionArea(null)
        return
      }

      isSelecting.current = false

      if (!pos || !selectionStart.current) {
        setTempDragState(null)
        selectionStart.current = null
        selectionEnd.current = null
        setTempSelectionArea(null)
        setInteractionMode(null)
        return
      }

      const distance = Math.abs(pos.y - selectionStart.current.y)
      if (distance < CLICK_THRESHOLD) {
        const clickTime = calcTime(startDate, pos)
        const previousDuration = getPreviousDuration()
        const defaultEnd = new Date(clickTime.getTime() + previousDuration)
        const { validated } = updateSelection(clickTime, defaultEnd)
        finalizeSelection(validated.start, validated.end)
      } else {
        const startY = Math.min(selectionStart.current.y, pos.y)
        const endY = Math.max(selectionStart.current.y, pos.y)
        const startTime = calcTime(startDate, { y: startY })
        const endTime = calcTime(startDate, { y: endY }, true)
        const { validated } = updateSelection(startTime, endTime)
        finalizeSelection(validated.start, validated.end)
      }

      selectionStart.current = null
      selectionEnd.current = null
      setTempSelectionArea(null)
      setTempDragState(null)
    },
    [startDate, getPreviousDuration, updateSelection, finalizeSelection],
  )

  // ========================================================================
  // Move/Resize Interaction Handlers
  // ========================================================================

  const startMoveResize = useCallback(
    (zone: InteractionZone, clientY: number, selectionIndex = -1): void => {
      if (zone === 'top') {
        setInteractionMode('resizing-top')
      } else if (zone === 'bottom') {
        setInteractionMode('resizing-bottom')
      } else {
        setInteractionMode('moving')
      }

      moveStartY.current = clientY
      setActiveSelectionIndex(selectionIndex)

      const selection = multiMode
        ? selectionIndex >= 0 && selectionIndex < selections.length
          ? selections[selectionIndex]
          : null
        : currentSelection

      originalSelection.current = selection ? { ...selection } : null

      if (isMobileView && zone === 'middle' && selection && timeLineContainerRef.current) {
        const bound = timeLineContainerRef.current.getBoundingClientRect()
        const touchRelativeY = clientY - bound.top
        const selectionStartMinutes = selection.start.getHours() * 60 + selection.start.getMinutes()
        const selectionTopY = (selectionStartMinutes / MINUTES_PER_SLOT) * SLOT_HEIGHT
        touchOffsetY.current = touchRelativeY - selectionTopY
      } else {
        touchOffsetY.current = null
      }

      if (selection) {
        setTempDragState({ ...selection })
      }
    },
    [currentSelection, selections, multiMode, isMobileView, timeLineContainerRef],
  )

  const handleMovingMove = useCallback(
    (clientY: number): void => {
      if (
        !originalSelection.current ||
        moveStartY.current === null ||
        !timeLineContainerRef.current
      )
        return

      const bound = timeLineContainerRef.current.getBoundingClientRect()

      if (isMobileView && touchOffsetY.current !== null) {
        const touchRelativeY = clientY - bound.top
        const selectionTopY = touchRelativeY - touchOffsetY.current
        const minutes = (selectionTopY / SLOT_HEIGHT) * MINUTES_PER_SLOT
        const duration =
          originalSelection.current.end.getTime() - originalSelection.current.start.getTime()

        const newStart = new Date(startDate)
        newStart.setHours(0, 0, 0, 0)
        newStart.setMinutes(Math.round(minutes))

        const newEnd = new Date(newStart.getTime() + duration)
        updateSelection(newStart, newEnd, true)
      } else {
        const currentRelativeY = clientY - bound.top
        const originalRelativeY = moveStartY.current - bound.top
        const deltaY = currentRelativeY - originalRelativeY
        const deltaMinutes = (deltaY / SLOT_HEIGHT) * MINUTES_PER_SLOT

        const newStart = new Date(originalSelection.current.start.getTime() + deltaMinutes * 60000)
        const newEnd = new Date(originalSelection.current.end.getTime() + deltaMinutes * 60000)
        updateSelection(newStart, newEnd, true)
      }
    },
    [timeLineContainerRef, updateSelection, isMobileView, startDate],
  )

  const handleResizingTopMove = useCallback(
    (clientY: number): void => {
      if (!originalSelection.current || moveStartY.current === null) return

      const deltaY = clientY - moveStartY.current
      const deltaMinutes = Math.round((deltaY / SLOT_HEIGHT) * MINUTES_PER_SLOT)
      const newStart = new Date(originalSelection.current.start.getTime() + deltaMinutes * 60000)

      updateSelection(newStart, originalSelection.current.end)
    },
    [updateSelection],
  )

  const handleResizingBottomMove = useCallback(
    (clientY: number): void => {
      if (!originalSelection.current || moveStartY.current === null) return

      const deltaY = clientY - moveStartY.current
      const deltaMinutes = Math.round((deltaY / SLOT_HEIGHT) * MINUTES_PER_SLOT)
      const newEnd = new Date(originalSelection.current.end.getTime() + deltaMinutes * 60000)

      updateSelection(originalSelection.current.start, newEnd)
    },
    [updateSelection],
  )

  const handleMoveResizeEnd = useCallback((): void => {
    if (tempDragState) {
      const snappedStart = snapToHalfHour(tempDragState.start)
      const snappedEnd = snapToHalfHour(tempDragState.end)
      const { validated } = updateSelection(snappedStart, snappedEnd, true)
      const allday = tempDragState.allday || false
      finalizeSelection(validated.start, validated.end, allday)
    }
    setTempDragState(null)
    setActiveSelectionIndex(-1)
    touchOffsetY.current = null
  }, [tempDragState, updateSelection, finalizeSelection])

  // ========================================================================
  // Mouse Event Handlers
  // ========================================================================

  const onTimelineMouseDown = useCallback(
    (e: React.MouseEvent | MouseEvent): void => {
      if (e.button !== 0) return
      const nativeEvent = getNativeMouseEvent(e)
      const pos = calcPointLocal(nativeEvent)
      if (pos) {
        startNewSelection(pos)
      }
    },
    [calcPointLocal, startNewSelection],
  )

  const onTimelineMouseMove = useCallback(
    (e: React.MouseEvent | MouseEvent): void => {
      if (!interactionMode) return

      e.stopPropagation()
      document.body.style.userSelect = 'none'

      const nativeEvent = getNativeMouseEvent(e)
      switch (interactionMode) {
        case 'selecting': {
          const pos = calcPointLocal(nativeEvent)
          if (pos) handleSelectingMove(pos)
          break
        }
        case 'moving':
          handleMovingMove(nativeEvent.clientY)
          break
        case 'resizing-top':
          handleResizingTopMove(nativeEvent.clientY)
          break
        case 'resizing-bottom':
          handleResizingBottomMove(nativeEvent.clientY)
          break
        default:
          break
      }
    },
    [
      interactionMode,
      calcPointLocal,
      handleSelectingMove,
      handleMovingMove,
      handleResizingTopMove,
      handleResizingBottomMove,
    ],
  )

  const onTimelineMouseUp = useCallback(
    (e: React.MouseEvent | MouseEvent): void => {
      if (!interactionMode) return

      e.preventDefault()
      e.stopPropagation()
      document.body.style.userSelect = 'auto'

      const nativeEvent = getNativeMouseEvent(e)
      if (interactionMode === 'selecting') {
        const pos = calcPointLocal(nativeEvent)
        handleSelectingEnd(pos)
      } else if (['moving', 'resizing-top', 'resizing-bottom'].includes(interactionMode)) {
        handleMoveResizeEnd()
      }

      setInteractionMode(null)
      moveStartY.current = null
      originalSelection.current = null
      touchOffsetY.current = null
      isSelecting.current = false
      selectionStart.current = null
      selectionEnd.current = null
    },
    [interactionMode, calcPointLocal, handleSelectingEnd, handleMoveResizeEnd],
  )

  // ========================================================================
  // Touch Event Handlers
  // ========================================================================

  const onTimelineTouchStart = useCallback(
    (e: React.TouchEvent | TouchEvent): void => {
      if (e.touches.length !== 1) return
      const touch = getNativeTouchEvent(e).touches[0]
      const pos = calcPointLocal(touch)
      if (pos) {
        startNewSelection(pos)
      }
    },
    [calcPointLocal, startNewSelection],
  )

  const onTimelineTouchMove = useCallback(
    (e: React.TouchEvent | TouchEvent): void => {
      if (!interactionMode || e.touches.length !== 1) {
        if (isMobileView && !interactionMode) {
          return
        }
      }

      const touch = getNativeTouchEvent(e).touches[0]

      if (isMobileView && interactionMode === 'selecting') {
        const pos = calcPointLocal(touch)
        if (!pos || !touchStartPos.current) return

        const deltaY = Math.abs(pos.y - touchStartPos.current.y)
        const deltaX = Math.abs((pos.x ?? 0) - (touchStartPos.current.x ?? 0))
        const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY)

        if (distance > CLICK_THRESHOLD) {
          hasMoved.current = true
          setInteractionMode(null)
          isSelecting.current = false
          selectionStart.current = null
          selectionEnd.current = null
          setTempSelectionArea(null)
          return
        } else {
          e.preventDefault()
          e.stopPropagation()
          handleSelectingMove(pos)
          return
        }
      }

      if (isMobileView) {
        if (['moving', 'resizing-top', 'resizing-bottom'].includes(interactionMode)) {
          e.preventDefault()
          e.stopPropagation()
        } else {
          return
        }
      } else {
        e.preventDefault()
        e.stopPropagation()
      }

      switch (interactionMode) {
        case 'selecting': {
          const pos = calcPointLocal(touch)
          if (pos) handleSelectingMove(pos)
          break
        }
        case 'moving':
          handleMovingMove(touch.clientY)
          break
        case 'resizing-top':
          handleResizingTopMove(touch.clientY)
          break
        case 'resizing-bottom':
          handleResizingBottomMove(touch.clientY)
          break
        default:
          break
      }
    },
    [
      interactionMode,
      calcPointLocal,
      handleSelectingMove,
      handleMovingMove,
      handleResizingTopMove,
      handleResizingBottomMove,
      isMobileView,
    ],
  )

  const onTimelineTouchEnd = useCallback(
    (e: React.TouchEvent | TouchEvent): void => {
      if (isMobileView && !interactionMode) {
        touchStartPos.current = null
        hasMoved.current = false
        return
      }

      e.preventDefault()
      e.stopPropagation()

      if (!interactionMode) return

      if (interactionMode === 'selecting') {
        if (!selectionStart.current) {
          setTempDragState(null)
          selectionStart.current = null
          selectionEnd.current = null
          setTempSelectionArea(null)
          setInteractionMode(null)
          touchStartPos.current = null
          hasMoved.current = false
          return
        }

        const endPos = selectionEnd.current || selectionStart.current
        const distance = Math.abs(endPos.y - selectionStart.current.y)

        if (isMobileView && !hasMoved.current && distance < CLICK_THRESHOLD) {
          const clickTime = calcTime(startDate, selectionStart.current)
          const previousDuration = getPreviousDuration()
          const defaultEnd = new Date(clickTime.getTime() + previousDuration)
          const { validated } = updateSelection(clickTime, defaultEnd)
          finalizeSelection(validated.start, validated.end)
        } else if (distance < CLICK_THRESHOLD && selectionStart.current) {
          const clickTime = calcTime(startDate, selectionStart.current)
          const previousDuration = getPreviousDuration()
          const defaultEnd = new Date(clickTime.getTime() + previousDuration)
          const { validated } = updateSelection(clickTime, defaultEnd)
          finalizeSelection(validated.start, validated.end)
        } else {
          const startY = Math.min(selectionStart.current.y, endPos.y)
          const endY = Math.max(selectionStart.current.y, endPos.y)
          const startTime = calcTime(startDate, { y: startY })
          const endTime = calcTime(startDate, { y: endY }, true)
          const { validated } = updateSelection(startTime, endTime)
          finalizeSelection(validated.start, validated.end)
        }

        selectionStart.current = null
        selectionEnd.current = null
        setTempSelectionArea(null)
        setTempDragState(null)
        touchStartPos.current = null
        hasMoved.current = false
      } else if (['moving', 'resizing-top', 'resizing-bottom'].includes(interactionMode)) {
        handleMoveResizeEnd()
      }

      setInteractionMode(null)
      moveStartY.current = null
      originalSelection.current = null
      touchOffsetY.current = null
      isSelecting.current = false
      selectionStart.current = null
      selectionEnd.current = null
      touchStartPos.current = null
      hasMoved.current = false
    },
    [
      interactionMode,
      startDate,
      getPreviousDuration,
      updateSelection,
      finalizeSelection,
      handleMoveResizeEnd,
      isMobileView,
    ],
  )

  // ========================================================================
  // Selection Mouse/Touch Handlers
  // ========================================================================

  const onSelectionMouseDown = useCallback(
    (e: React.MouseEvent | MouseEvent, zone: InteractionZone, selectionIndex = -1): void => {
      e.stopPropagation()
      const nativeEvent = getNativeMouseEvent(e)
      startMoveResize(zone, nativeEvent.clientY, selectionIndex)
    },
    [startMoveResize],
  )

  const onSelectionTouchStart = useCallback(
    (e: React.TouchEvent | TouchEvent, zone: InteractionZone, selectionIndex = -1): void => {
      e.stopPropagation()
      if (e.touches.length !== 1) return

      if (isMobileView) {
        e.preventDefault()
      }

      const touch = e.touches[0]
      startMoveResize(zone, touch.clientY, selectionIndex)
    },
    [startMoveResize, isMobileView],
  )

  return {
    interactionMode,
    tempSelectionArea,
    tempDragState,
    activeSelectionIndex,
    onTimelineMouseDown,
    onTimelineMouseMove,
    onTimelineMouseUp,
    onTimelineTouchStart,
    onTimelineTouchMove,
    onTimelineTouchEnd,
    onSelectionMouseDown,
    onSelectionTouchStart,
  }
}

export default useTimelineInteraction
